import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  ArrowUpRight,
  Sparkles,
  Mic,
  ChevronRight,
  Brain,
  ClipboardList,
  History,
  LibraryBig,
  Bookmark,
  FileClock,
  Plus,
  Search,
  Command,
  Maximize2,
} from "lucide-react";
import { GlassCard, GlassCardHeader } from "@/components/system/GlassCard";
import ProvenanceTag from "@/components/system/ProvenanceTag";
import { StageChip, ComplexityChip } from "@/components/system/Chips";
import VoiceInput from "@/components/system/VoiceInput";
import Ni2Icon from "@/components/system/Ni2Icon";
import Ni2ContextWorkspace from "@/components/ni2/Ni2ContextWorkspace";
import { NI2_CONTEXTUAL_FILES_ENABLED } from "@/constants/featureFlags";
import { useWorkspaceMode } from "@/context/WorkspaceModeContext";
import {
  getSurgeonCaseCards,
  getRecentClinicalCaseCards,
  getRecentMemoryFeed,
  getUpcomingReviews,
  getTodaysTimeline,
} from "@/data/viewModels";
import { DASH } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FALLBACK_SUMMARY = {
  active_cases: 5,
  reflections_this_week: 14,
  memory_entries: 108,
  hours_indexed: 62.4,
  provenance_links: 1284,
  upcoming_reviews: 3,
};

const KIND_META = {
  reflection:      { label: "Reflection",     Icon: Sparkles,      kind: "amber" },
  "operative-note":{ label: "Operative note", Icon: ClipboardList, kind: "amber" },
  "m-and-m":       { label: "M&M note",       Icon: FileClock,     kind: "amber" },
  literature:      { label: "Literature",     Icon: LibraryBig,    kind: "blue"  },
};

const GREETINGS = [
  {
    title: "Good morning, Dr. Rao.",
    subtitle: "OR-4 begins in 27 minutes.",
  },
  {
    title: "Welcome back.",
    subtitle: "Ni² prepared today's surgical briefing.",
  },
  {
    title: "Good morning.",
    subtitle: "Two active surgical cases require your review today.",
  },
];

function pickGreeting() {
  const h = new Date().getHours();
  const idx = h < 10 ? 0 : h < 14 ? 1 : 2;
  return GREETINGS[idx];
}

export default function Dashboard() {
  const [summary, setSummary] = useState(FALLBACK_SUMMARY);
  const [heroFocused, setHeroFocused] = useState(false);
  const { mode: workspace, setMode: setWorkspace } = useWorkspaceMode();
  const scrollRef = useRef(0);
  const greeting = pickGreeting();

  // Preserve scroll + panel state when swapping into Ni² Workspace and back.
  const scrollHost = () =>
    typeof document !== "undefined"
      ? document.querySelector('[data-testid="workspace"]')
      : null;

  const openNi2 = () => {
    const el = scrollHost();
    if (el) scrollRef.current = el.scrollTop;
    setHeroFocused(false);
    setWorkspace("ni2");
    requestAnimationFrame(() => {
      const el2 = scrollHost();
      if (el2) el2.scrollTop = 0;
    });
  };

  const closeNi2 = () => {
    setWorkspace("dashboard");
    // Restore scroll after the swap animation begins so it feels continuous.
    requestAnimationFrame(() => {
      const el = scrollHost();
      if (el) el.scrollTop = scrollRef.current;
    });
  };

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/dashboard/summary`)
      .then((res) => alive && res?.data && setSummary(res.data))
      .catch(() => { /* keep fallback for demo resilience */ });
    return () => { alive = false; };
  }, []);

  const CASES           = getSurgeonCaseCards();
  const RECENT_MEMORY   = getRecentMemoryFeed();
  const UPCOMING_REVIEWS= getUpcomingReviews();
  const TIMELINE_TODAY  = getTodaysTimeline();

  /* Populated by the workbook's most recent clinically-relevant events —
     latest Clinical Encounter per case, or Date of Surgery as a
     fallback. See viewModels.getRecentClinicalCaseCards. */
  const upcoming = getRecentClinicalCaseCards(4);

  // Both workspaces stay mounted so the Dashboard's scroll position, panel
  // state and any transient UI (heroFocused, VoiceInput mid-conversation, etc.)
  // survive the round-trip into Ni² Workspace.
  const isNi2 = workspace === "ni2";

  return (
    <div className="relative" data-testid={DASH.root}>
      {/* Dashboard pane */}
      <div
        aria-hidden={isNi2}
        className="nn-workspace-pane"
        data-active={!isNi2}
        style={{
          display: isNi2 ? "none" : "block",
        }}
      >
        <div className="relative flex flex-col">
          {/* Dim overlay for the rest of the dashboard when hero is focused */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-[5]"
            style={{
              background: "rgba(6, 10, 18, 0.05)",
              opacity: heroFocused ? 1 : 0,
              transition: "opacity 220ms ease",
            }}
          />

      {/* Greeting / context header */}
      <section className="nn-enter flex items-end justify-between gap-8 pt-2">
        <div>
          <div className="nn-eyebrow">Wednesday · Feb 18, 2026 · 06:14 local</div>
          <h1
            className="mt-3 nn-h1"
            style={{
              color: "#ffffff",
              textShadow: "0 1px 24px rgba(255,255,255,0.05)",
              letterSpacing: "-0.024em",
            }}
          >
            {greeting.title}
          </h1>
          <p className="mt-3 max-w-2xl text-[13px] leading-[21px] text-[hsl(var(--nn-ink-muted))]">
            {greeting.subtitle} Your surgical memory is current — 14 reflections have been
            captured this week and provenance links are up to date.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button className="nn-btn nn-btn-ghost">
            <Mic className="h-3.5 w-3.5" strokeWidth={1.6} /> Voice note
          </button>
          <button className="nn-btn">
            <Plus className="h-3.5 w-3.5" strokeWidth={1.6} /> New case
          </button>
          <button className="nn-btn nn-btn-primary">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={1.8} /> Start pre-case review
          </button>
        </div>
      </section>

      {/* Hero Ni² command surface — primary interaction point */}
      <section className="nn-enter nn-enter-1 relative z-10 mt-2 px-2">
        <button
          data-testid="dashboard-hero-search"
          onClick={openNi2}
          onMouseEnter={() => setHeroFocused(true)}
          onMouseLeave={() => setHeroFocused(false)}
          onFocus={() => setHeroFocused(true)}
          onBlur={() => setHeroFocused(false)}
          className="group nn-hero-search flex w-full items-center gap-3.5 rounded-2xl text-left"
          style={{
            padding: heroFocused ? "14px 26px" : "11px 24px",
            transform: heroFocused ? "translateY(-1px)" : "none",
            transition:
              "padding 200ms cubic-bezier(0.32, 0.72, 0.24, 1), border-color 180ms ease, box-shadow 180ms ease, background 180ms ease, transform 180ms ease",
          }}
        >
          {/* Glass capsule behind the Ni² icon */}
          <span
            className="grid h-9 w-9 shrink-0 place-items-center rounded-xl"
            style={{
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.02))",
              border: "1px solid rgba(255,255,255,0.12)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.16)",
            }}
          >
            <Ni2Icon size={26} />
          </span>

          {/* Placeholder hierarchy */}
          <span className="flex-1 truncate text-[14px] leading-[20px]">
            <span
              className="font-semibold tracking-tight text-white"
              style={{ letterSpacing: "-0.01em" }}
            >
              Ask Ni²
            </span>
            <span className="text-[hsl(var(--nn-ink-muted))]">
              {" "}about cases, reflections, imaging and literature…
            </span>
          </span>

          <Search className="h-4 w-4 text-[hsl(var(--nn-ink-muted))]" strokeWidth={1.6} />

          {/* Open Ni² Workspace affordance */}
          <span
            data-testid="ni2-expand"
            aria-label="Open Ni² Workspace"
            title="Open Ni² Workspace"
            className="grid h-6 w-6 place-items-center rounded-md border border-white/15 bg-white/[0.06] text-[hsl(var(--nn-ink-muted))] transition-colors group-hover:border-white/25 group-hover:text-[hsl(var(--nn-ink))]"
          >
            <Maximize2 className="h-3 w-3" strokeWidth={1.8} />
          </span>

          <span className="hidden items-center gap-1 sm:flex">
            <kbd className="grid h-6 w-6 place-items-center rounded-md border border-white/20 bg-white/[0.08] font-mono text-[10px]">
              <Command className="h-3 w-3" strokeWidth={2} />
            </kbd>
            <kbd className="grid h-6 min-w-6 place-items-center rounded-md border border-white/20 bg-white/[0.08] px-1 font-mono text-[10px]">
              K
            </kbd>
          </span>
        </button>
      </section>

      {/* KPI row */}
      <section className="nn-enter nn-enter-2 mt-3 grid grid-cols-4 gap-7">
        <Kpi label="Active cases"          value={summary.active_cases}
             hint="pre-op · intra-op · post-op"                          Icon={Brain}     accent="blue" />
        <Kpi label="Reflections this week" value={summary.reflections_this_week}
             hint="captured across 4 cases"                              Icon={Sparkles}  accent="amber" />
        <Kpi label="Memory entries"        value={summary.memory_entries}
             hint={`${summary.hours_indexed} h indexed`}                 Icon={History}   accent="plain" />
        <Kpi label="Provenance links"      value={summary.provenance_links.toLocaleString()}
             hint={`${summary.upcoming_reviews} upcoming reviews`}       Icon={Bookmark}  accent="plain" />
      </section>

      {/* Main two-column */}
      <section className="grid grid-cols-12 gap-7">
        {/* Recent clinical cases — sorted by most recent Clinical
            Encounter, falling back to Date of Surgery. Populated
            entirely from the workbook; no synthetic filler. */}
        <GlassCard className="nn-enter nn-enter-2 nn-lift col-span-7 !p-6">
          <GlassCardHeader
            title="Recent clinical cases"
            hint="Sorted by latest encounter or operative date"
            action={
              <button className="nn-btn nn-btn-ghost">
                View all cases <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.6} />
              </button>
            }
          />
          <div className="flex flex-col gap-2.5">
            {upcoming.map((c, i) => (
              <UpcomingCase key={c.id} c={c} idx={i} />
            ))}
          </div>
        </GlassCard>

        {/* Today's timeline — promoted from row 2 into the space vacated by
            the removed Ni² card. The dashboard now has a single Ni² entry
            point (the hero command surface). */}
        <GlassCard className="nn-enter nn-enter-2 nn-lift col-span-5 !p-6">
          <GlassCardHeader
            title="Today's timeline"
            hint="OR-4 · NN-2036-E · MCA aneurysm"
            action={<span className="nn-chip nn-chip-amber">Live capture</span>}
          />
          <ol className="relative ml-3 border-l border-white/10">
            {TIMELINE_TODAY.map((ev, i) => (
              <li
                key={ev.t}
                data-testid={DASH.timelineItem}
                className="relative mb-4 pl-5 last:mb-0"
              >
                <span
                  className="absolute -left-[6px] top-1.5 h-2.5 w-2.5 rounded-full border border-white/20"
                  style={{
                    background:
                      i === 2
                        ? "hsl(var(--nn-amber))"
                        : i === 3
                          ? "hsl(var(--nn-blue))"
                          : "rgba(255,255,255,0.35)",
                    boxShadow:
                      i === 2 ? "0 0 10px hsl(var(--nn-amber) / 0.6)" : "none",
                  }}
                />
                <div className="text-[10.5px] font-mono text-[hsl(var(--nn-ink-faint))]">
                  {ev.t}
                </div>
                <div className="text-[13px] font-medium text-[hsl(var(--nn-ink))]">
                  {ev.label}
                </div>
                <div className="text-[12px] text-[hsl(var(--nn-ink-muted))]">
                  {ev.detail}
                </div>
              </li>
            ))}
          </ol>
        </GlassCard>

        {/* Recent memory feed — full width now that Ni² is a single hero surface */}
        <GlassCard className="nn-enter nn-enter-3 nn-lift col-span-12 !p-6">
          <GlassCardHeader
            title="Recent surgical memory"
            hint="Reflections, operative notes and M&M threads"
            action={
              <div className="flex items-center gap-1.5">
                <span className="nn-chip">All</span>
                <span className="nn-chip nn-chip-amber">Mine</span>
                <span className="nn-chip">Shared</span>
              </div>
            }
          />
          <div className="flex flex-col divide-y divide-white/[0.05]">
            {RECENT_MEMORY.map((m) => {
              const meta = KIND_META[m.kind];
              const Icon = meta.Icon;
              return (
                <div
                  key={m.id}
                  data-testid={DASH.memoryEntry}
                  className="group flex items-start gap-4 py-4 first:pt-0 last:pb-0"
                >
                  <div className="mt-0.5 grid h-9 w-9 place-items-center rounded-xl border border-white/[0.08] bg-white/[0.04]">
                    <Icon
                      className="h-4 w-4"
                      strokeWidth={1.6}
                      style={{
                        color:
                          meta.kind === "amber"
                            ? "hsl(var(--nn-amber))"
                            : "hsl(var(--nn-blue))",
                      }}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-[11px] font-mono text-[hsl(var(--nn-ink-faint))]">
                      <span>{m.time}</span>
                      <span>·</span>
                      <span className="text-[hsl(var(--nn-ink-muted))]">{meta.label}</span>
                      <span>·</span>
                      <ProvenanceTag label="Case" ref={m.caseId} testId={DASH.provenanceTag} />
                    </div>
                    <p className="mt-1.5 text-[14px] leading-[22px] text-[hsl(var(--nn-ink))]">
                      {m.excerpt}
                    </p>
                    <div className="mt-1.5 text-[12px] text-[hsl(var(--nn-ink-muted))]">
                      {m.caseLabel}
                    </div>
                  </div>
                  <button className="nn-icon-btn opacity-0 transition-opacity group-hover:opacity-100">
                    <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.6} />
                  </button>
                </div>
              );
            })}
          </div>
        </GlassCard>

        {/* Upcoming reviews */}
        <GlassCard className="nn-enter nn-enter-4 nn-lift col-span-12 !p-6">
          <GlassCardHeader
            title="Pre-case reviews & M&M candidates"
            hint="Assembled from your captured memory and departmental agenda"
            action={
              <button className="nn-btn nn-btn-ghost">
                Open M&M Builder <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.6} />
              </button>
            }
          />
          <div className="grid grid-cols-3 gap-4">
            {UPCOMING_REVIEWS.map((r) => (
              <div
                key={r.id}
                className="nn-glass-inset group flex flex-col gap-3 p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-white/15"
              >
                <div className="flex items-center justify-between text-[10.5px] font-mono text-[hsl(var(--nn-ink-faint))]">
                  <span>{r.id}</span>
                  <span>{r.when}</span>
                </div>
                <div className="text-[13.5px] font-medium leading-snug text-[hsl(var(--nn-ink))]">
                  {r.title}
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <ProvenanceTag label="Case" ref={r.caseId} />
                  <span className="text-[11px] text-[hsl(var(--nn-ink-muted))]">
                    {r.room}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      </section>

      <div className="pb-4" />
        </div>
      </div>

      {/* Ni² Workspace pane — kept mounted so it can animate in/out cleanly */}
      <div
        aria-hidden={!isNi2}
        className="nn-workspace-pane"
        data-active={isNi2}
        style={{
          display: isNi2 ? "block" : "none",
        }}
        data-testid="ni2-workspace"
      >
        <Ni2ContextWorkspace onBack={closeNi2} enabled={NI2_CONTEXTUAL_FILES_ENABLED} />
      </div>
    </div>
  );
}

/* ---------- Sub-components ---------- */

function Kpi({ label, value, hint, Icon, accent = "plain" }) {
  const accentColor =
    accent === "blue"  ? "hsl(var(--nn-blue))"
    : accent === "amber" ? "hsl(var(--nn-amber))"
    : "hsl(var(--nn-ink-muted))";
  return (
    <GlassCard testId={DASH.kpi} className="nn-lift relative overflow-hidden !px-5 !py-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="nn-eyebrow">{label}</div>
          <div
            className="mt-2.5 text-[30px] font-semibold leading-none tracking-[-0.024em] text-white"
            style={{ textShadow: "0 1px 20px rgba(255,255,255,0.04)" }}
          >
            {value}
          </div>
          <div className="mt-2.5 text-[12px] leading-[17px] text-[hsl(var(--nn-ink-muted))]">
            {hint}
          </div>
        </div>
        <div
          className="grid h-9 w-9 place-items-center rounded-xl border border-white/[0.1] bg-white/[0.05]"
          style={{ color: accentColor }}
        >
          <Icon className="h-4 w-4" strokeWidth={1.6} />
        </div>
      </div>
      <div
        className="pointer-events-none absolute -bottom-8 -right-8 h-28 w-28 rounded-full opacity-[0.13] blur-2xl"
        style={{ background: accentColor }}
      />
    </GlassCard>
  );
}

function UpcomingCase({ c, idx }) {
  return (
    <div
      data-testid={DASH.upcomingCase}
      className="group grid grid-cols-[auto_1fr_auto_auto_auto] items-center gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-2 transition-all duration-300 hover:-translate-y-0.5 hover:border-white/[0.14] hover:bg-white/[0.05]"
    >
      <div className="grid h-9 w-9 place-items-center rounded-xl border border-white/[0.06] bg-white/[0.04] font-mono text-[11px] text-[hsl(var(--nn-ink-muted))]">
        {String(idx + 1).padStart(2, "0")}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-medium text-[hsl(var(--nn-ink))]">
            {c.patient} · {c.age}
            {c.sex}
          </span>
          <span className="text-[10.5px] font-mono text-[hsl(var(--nn-ink-faint))]">
            {c.mrn}
          </span>
        </div>
        <div className="mt-0.5 truncate text-[12.5px] text-[hsl(var(--nn-ink-muted))]">
          {c.diagnosis} · <span className="text-[hsl(var(--nn-ink))]/80">{c.procedure}</span>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <StageChip stage={c.stage} />
        <ComplexityChip complexity={c.complexity} />
      </div>
      <div className="min-w-[96px] text-right">
        <div className="text-[11px] font-mono text-[hsl(var(--nn-ink-muted))]">{c.date}</div>
        <div className="text-[10.5px] text-[hsl(var(--nn-ink-faint))]">
          {c.reflections} refl · {c.memory} mem
        </div>
      </div>
      <div
        className="flex items-center gap-1 pl-1 text-[10.5px] font-medium text-[hsl(var(--nn-ink-muted))] opacity-0 transition-opacity duration-200 group-hover:opacity-100"
        aria-hidden="true"
      >
        <span className="hidden lg:inline">Open case</span>
        <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.8} />
      </div>
    </div>
  );
}
